import { numberFormatter } from 'components/AgGrid/utils';
import { TextWithTooltip } from 'components/WithTooltip';
import type { WorkOrderChangeSeasonData, WorkOrderSeasonData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { CodeCell } from 'pages/Project/Common/CodeCell';
import { Button, IconArrowDown, IconArrowUp } from 'uikit';
import { formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';

import { CheckBoxRenderer, NoteColumnRenderer, SeasonRenderer } from '../cellRenderers';
import { CHAPTER_CONST, seasonValueGetter, TYPE_CONST } from '../index';

const defineBalance = (limit: number, balance: number) => {
    const amount = limit - balance;

    if (balance < 0) {
        return {
            color: '#EB3333',
            tooltip: 'Остаток по прочим затратам отрицательный',
        };
    }

    return limit * 0.3 < amount
        ? { color: '#EB6D00', tooltip: 'Остаток по прочим затратам меньше 30%' }
        : { color: '' };
};

const DEFAULT_SEASON_WIDTH = 128;

export const useAddWorkOrderOtherColumns = ({
    workOrderSeasons,
    control,
    visibleRowIds,
    changeVisibleRowIds,
    nameFilter,
    workOrderStatus,
    isWorkOrderChange,
}: {
    workOrderSeasons: (WorkOrderSeasonData | WorkOrderChangeSeasonData)[];
    control: Control<any>;
    visibleRowIds: any;
    changeVisibleRowIds: any;
    nameFilter?: any;
    workOrderStatus?: WorkOrderStatusEnum;
    isWorkOrderChange?: boolean;
}) => {
    const includeValueColumns = useMemo(
        () =>
            workOrderSeasons?.map((season) => ({
                headerName: season?.year,
                width: DEFAULT_SEASON_WIDTH,
                cellRendererParams: { control, season },
                cellRenderer: SeasonRenderer,
            })),
        [control, workOrderSeasons],
    );

    return useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 50,
                lockPosition: 'left',
                pinned: 'left',
                resizable: false,
                cellRendererParams: {
                    control,
                    isWorkOrderChange,
                },
                cellRenderer: CheckBoxRenderer,
            },
            {
                headerName: 'Наименование',
                field: 'name',
                minWidth: 350,
                flex: 1,
                colSpan: (params: any) => {
                    if (params?.data?.nestLevel === 0) {
                        return 1;
                    }

                    return 4;
                },
                ...nameFilter,
                cellRenderer: ({ data }: any) => {
                    if (data?.nestLevel === 0) {
                        const areChildrenShown = !!(data?.id && visibleRowIds?.[data.id]);

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <Button
                                    size="s"
                                    view="clear"
                                    onlyIcon
                                    iconRight={areChildrenShown ? IconArrowUp : IconArrowDown}
                                    onClick={changeVisibleRowIds?.(data?.id)}
                                    style={{ color: 'var(--text-dark-translucent)' }}
                                />

                                <CodeCell status={data.status!} statusLabel={data?.name} />
                            </div>
                        );
                    }

                    return <CodeCell status={data.status!} statusLabel={data?.name} />;
                },
            },
            {
                headerName: 'Раздел',
                field: 'chapter',
                width: 100,
                valueGetter: ({ data }: any) =>
                    (data?.chapter && CHAPTER_CONST[data.chapter]) || (data?.contractOtherCostId && 'Лимиты Заказчика'),
            },
            {
                headerName: 'Способ расчета',
                field: 'type',
                width: 100,
                valueGetter: ({ data }: any) =>
                    (data?.calculatingType && TYPE_CONST[data.calculatingType]) ||
                    (data?.calculatingType && `${data?.calculatingTypeValue} %`),
            },
            {
                headerName: 'Лимит по Договору',
                children: workOrderSeasons?.map((season) => ({
                    headerName: season?.year,
                    width: DEFAULT_SEASON_WIDTH,
                    valueGetter: ({ data }) => seasonValueGetter(data, season, 'planCost'),
                    valueFormatter: numberFormatter(2),
                    type: 'numericColumn',
                })),
            },
            {
                headerName: 'Остаток по Договору',
                children: workOrderSeasons?.map((season) => ({
                    headerName: season?.year,
                    width: DEFAULT_SEASON_WIDTH,
                    valueGetter: ({ data }) => seasonValueGetter(data, season, 'remainderCost'),
                    valueFormatter: numberFormatter(2),
                    cellStyle: {
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        height: '100%',
                    },
                    type: 'numericColumn',
                    cellRenderer: (params: any) => {
                        const { remainderCost = '', planCost = '' } = seasonValueGetter(params?.data, season) ?? {};

                        const { color, tooltip } = defineBalance(planCost, remainderCost);
                        const labelText = formatNumberToRu(remainderCost, 2);

                        return tooltip ? (
                            <TextWithTooltip tooltipText={tooltip} text={labelText} tooltipColor={color} />
                        ) : (
                            labelText
                        );
                    },
                })),
            },
            ...(workOrderStatus === WorkOrderStatusEnum.OnFormation
                ? [
                      {
                          headerName: 'Включить в НЗ',
                          children: includeValueColumns,
                      },
                  ]
                : []),
            ...(isWorkOrderChange
                ? [
                      {
                          headerName: 'Включить в Изм. к НЗ',
                          children: includeValueColumns,
                      },
                  ]
                : []),
            ...(workOrderStatus === WorkOrderStatusEnum.InProgress || isWorkOrderChange
                ? [
                      {
                          headerName: 'Примечание',
                          cellRenderer: NoteColumnRenderer,
                          cellRendererParams: {
                              control,
                          },
                      },
                  ]
                : []),
        ],
        [
            control,
            nameFilter,
            workOrderSeasons,
            workOrderStatus,
            includeValueColumns,
            isWorkOrderChange,
            visibleRowIds,
            changeVisibleRowIds,
        ],
    );
};
